import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { HelpCircle, Check, X } from "lucide-react";

export default function QuizSection({ questions, ageGroup, lessonId, t }) {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [saved, setSaved] = useState(false);
  const isLittleKid = ageGroup === "little";

  const handleAnswer = (questionIndex, optionIndex) => {
    if (showResults) return;
    setAnswers(prev => ({ ...prev, [questionIndex]: optionIndex }));
  };

  const checkAnswers = async () => {
    setShowResults(true);
    if (lessonId && !saved) {
      try {
        await axios.post(`${API}/lessons/${lessonId}/quiz`, { answers });
        setSaved(true);
      } catch (e) {
        console.error("Failed to save quiz answers:", e);
      }
    }
  };

  const resetQuiz = () => {
    setAnswers({});
    setShowResults(false);
    setSaved(false);
  };

  const allAnswered = Object.keys(answers).length === questions.length;
  const correctCount = questions.filter((q, i) => answers[i] === q.correct_index).length;

  return (
    <div className="bg-white rounded-[28px] shadow-xl mb-5 overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
      <div className="p-5 relative" style={{ background: "linear-gradient(90deg, #EF476F, #9B5DE5)" }}>
        <div className="absolute -top-2 right-4 text-3xl opacity-50">🧠</div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <HelpCircle size={24} color="white" strokeWidth={2.5} />
          </div>
          <h3 className="font-black text-white text-xl" style={{ fontFamily: "'Baloo 2', cursive" }}>
            {isLittleKid ? "Quiz Time! 🎯" : (t ? t('quiz_time') : "Test Your Knowledge!")}
          </h3>
        </div>
      </div>
      
      <div className="p-5 space-y-5">
        {questions.map((q, qIndex) => (
          <div key={qIndex} className="space-y-3">
            <p className="font-black text-[#073B4C] text-base flex items-start gap-2" 
              style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }}>
              <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-black"
                style={{ background: "#9B5DE5" }}>
                {qIndex + 1}
              </span>
              <span>{q.question}</span>
            </p>
            <div className="space-y-2 ml-9">
              {q.options?.map((option, oIndex) => {
                const isSelected = answers[qIndex] === oIndex;
                const isCorrect = q.correct_index === oIndex;
                const showCorrect = showResults && isCorrect;
                const showWrong = showResults && isSelected && !isCorrect;
                
                return (
                  <button
                    key={oIndex}
                    onClick={() => handleAnswer(qIndex, oIndex)}
                    disabled={showResults}
                    className={`w-full text-left p-4 rounded-2xl border-3 transition-all flex items-center gap-3 font-bold ${
                      showCorrect ? 'border-green-500 bg-green-50 border-2' :
                      showWrong ? 'border-red-500 bg-red-50 border-2' :
                      isSelected ? 'border-[#118AB2] bg-blue-50 border-2' :
                      'border-gray-200 hover:border-[#9B5DE5] border-2'
                    }`}
                    style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }}
                    data-testid={`quiz-option-${qIndex}-${oIndex}`}
                  >
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                      showCorrect ? 'border-green-500 bg-green-500 text-white' :
                      showWrong ? 'border-red-500 bg-red-500 text-white' :
                      isSelected ? 'border-[#118AB2] bg-[#118AB2] text-white' :
                      'border-gray-300'
                    }`}>
                      {showCorrect ? <Check size={16} /> : showWrong ? <X size={16} /> : String.fromCharCode(65 + oIndex)}
                    </span>
                    <span className="text-gray-700">{option}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Results / Submit */}
        {showResults ? (
          <div className="pt-4 border-t-2 border-dashed border-gray-200">
            <div className={`rounded-2xl p-5 text-center relative overflow-hidden ${
              correctCount === questions.length ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              {correctCount === questions.length && (
                <>
                  <div className="absolute top-1 left-2 text-2xl sparkle">🌟</div>
                  <div className="absolute top-1 right-2 text-2xl sparkle" style={{ animationDelay: '0.3s' }}>⭐</div>
                </>
              )}
              <p className="font-black text-2xl" style={{ fontFamily: "'Baloo 2', cursive" }}>
                {correctCount === questions.length 
                  ? (isLittleKid ? "WOW! Perfect! 🎉" : (t ? t('perfect') : 'Perfect!'))
                  : `${correctCount}/${questions.length} ${t ? t('correct') : 'Correct'}!`}
              </p>
              <p className="text-base text-gray-600 mt-2 font-semibold" style={{ fontFamily: "'Comic Neue', cursive" }}>
                {correctCount === questions.length 
                  ? (isLittleKid ? "You're a super star! ⭐" : (t ? t('perfect_score') : "You really understood today's lesson!"))
                  : (isLittleKid ? "Good try! Let's do it again! 💪" : (t ? t('good_effort') : "Great effort! Review the lesson and try again!"))}
              </p>
            </div>
            <button onClick={resetQuiz}
              className="w-full mt-4 text-[#9B5DE5] font-black py-3 text-base hover:bg-purple-50 rounded-xl transition-all"
              style={{ fontFamily: "'Baloo 2', cursive" }}
              data-testid="quiz-reset-btn">
              {isLittleKid ? "Try Again! 🔄" : (t ? t('try_again_quiz') : 'Try Again')}
            </button>
          </div>
        ) : (
          <button 
            onClick={checkAnswers}
            disabled={!allAnswered}
            className={`w-full py-4 rounded-2xl font-black text-white text-lg transition-all border-b-4 active:border-b-0 active:translate-y-1 ${
              allAnswered 
                ? 'bg-gradient-to-r from-[#EF476F] to-[#9B5DE5]' 
                : 'bg-gray-300 cursor-not-allowed border-gray-400'
            }`}
            style={{ fontFamily: "'Baloo 2', cursive", borderColor: allAnswered ? "#9B3DAD" : "#999" }}
            data-testid="quiz-submit-btn"
          >
            {isLittleKid ? "Check My Answers! ✨" : (t ? t('check_my_answers') : 'Check My Answers!')}
          </button>
        )}
      </div>
    </div>
  );
}
